import React, { useEffect, useState } from "react";
import { appThemaColors } from "../../../common/colorConst";
import AppBar from "@mui/material/AppBar";
import Box from "@mui/material/Box";
import CssBaseline from "@mui/material/CssBaseline";
import IconButton from "@mui/material/IconButton";
import MenuIcon from "@mui/icons-material/Menu";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import DrawerMenu from "./DrawerMenu";
import PersonIcon from "@mui/icons-material/Person";
import LogoutIcon from "@mui/icons-material/Logout";

import { Auth } from "aws-amplify";
import jwtDecode from "jwt-decode";
import { Menu, MenuItem } from "@mui/material";
import { useNavigate } from "react-router-dom";
import {
  AppVariants,
  headerTitleOf,
  titleColorOf,
} from "../../../common/AppVariants";
import { getJwtTokenFromIdToken } from "../../pages/auth/AuthFanc";

const drawerWidth = 240;

interface Props {
  window?: () => Window;
  appVariant: AppVariants;
}

const Header = (props: Props) => {
  const navigate = useNavigate();

  const { window, appVariant } = props;
  const [drawerOpen, setDrawerOpen] = React.useState(false);

  const [loggedInID, setLoggedInID] = useState<string | undefined>(undefined);
  useEffect(() => {
    const fetchLoggedInID = async () => {
      const jwtToken = await getJwtTokenFromIdToken();

      try {
        // JWTトークンからセッションを取得し、cognito:username をデコード
        const session = await Auth.currentSession();
        const idToken = session.getIdToken();
        const decodedToken: any = jwtDecode(idToken.getJwtToken());
        const cognitoUsername = decodedToken["cognito:username"];
        console.log("Decoded Token:", decodedToken);
        console.log("Cognito Username:", cognitoUsername);

        let response;
        let data;

        if (
          appVariant === AppVariants.SdcAdmin ||
          appVariant === AppVariants.Fulltech
        ) {
          // SdcAdminとFulltechはcognito:usernameをそのまま表示
          setLoggedInID(cognitoUsername);
          console.log(
            "AppVariant: SdcAdmin/Fulltech, LoggedInID:",
            cognitoUsername
          );
        } else if (
          appVariant === AppVariants.SocialOperator ||
          appVariant === AppVariants.AutolockOperator
        ) {
          // SocialOperatorとAutolockOperatorは/operators/self-infoを取得
          response = await fetch(
            process.env.REACT_APP_API_ENDPOINT + "/operators/self-info",
            {
              headers: {
                "Content-Type": "application/json",
                Authorization: jwtToken,
              },
            }
          );

          if (!response.ok) {
            throw new Error(`HTTP error! Status: ${response.status}`);
          }

          const responseText = await response.text();
          console.log("Response Text:", responseText);

          data = JSON.parse(responseText);
          console.log("Parsed Data:", data);

          // operatorNameとdepartmentNameを確認して表示
          if (data && data.operatorName) {
            const departmentName = data.departmentName ? data.departmentName : '部署名未登録'
            setLoggedInID(
              `${data.operatorName}（${departmentName}）【${cognitoUsername}】`
            );
            console.log(
              "AppVariant: SocialOperator/AutolockOperator, LoggedInID:",
              `${data.operatorName}（${departmentName}）【${cognitoUsername}】`
            );
          } else {
            throw new Error(
              "Invalid data structure: operatorName is missing"
            );
          }
        } else if (appVariant === AppVariants.Owner) {
          // Ownerは/owners/self-infoを取得
          response = await fetch(
            process.env.REACT_APP_API_ENDPOINT + "/owners/self-info",
            {
              headers: {
                "Content-Type": "application/json",
                Authorization: jwtToken,
              },
            }
          );

          if (!response.ok) {
            throw new Error(`HTTP error! Status: ${response.status}`);
          }

          const responseText = await response.text();
          console.log("Response Text:", responseText);

          data = JSON.parse(responseText);
          console.log("Parsed Data:", data);

          // ownerNameとdepartmentNameを確認して表示
          if (data && data.ownerName) {
            const departmentName = data.departmentName ? data.departmentName : '部署名未登録'
            setLoggedInID(
              `${data.ownerName}（${departmentName}）【${cognitoUsername}】`
            );
            console.log(
              "AppVariant: Owner, LoggedInID:",
              `${data.ownerName}（${departmentName}）【${cognitoUsername}】`
            );
          } else {
            throw new Error(
              "Invalid data structure: ownerName is missing"
            );
          }
        }
      } catch (error) {
        console.error("Error fetching logged-in ID:", error);
        setLoggedInID("取得エラー");
      }
    };

    fetchLoggedInID();
  }, [appVariant]);

  const [anchorEl, setAnchorEl] = React.useState<undefined | HTMLElement>(
    undefined
  );

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(undefined);
  };

  const signOut = async () => {
    try {
      await Auth.signOut();
      navigate(`/`);
    } catch (error) {
      console.error("Error signing out: ", error);
    }
  };

  const handleDrawerToggle = () => {
    setDrawerOpen(!drawerOpen);
  };

  const container =
    window !== undefined ? () => window().document.body : undefined;

  return (
    <Box sx={{ display: "flex" }}>
      <CssBaseline />
      <AppBar
        sx={{
          backgroundColor: titleColorOf(appVariant) || appThemaColors.default,
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
          >
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" noWrap component="div">
            {headerTitleOf(appVariant)}
          </Typography>
          <Box
            display={"flex"}
            alignItems={"center"}
            sx={{ marginLeft: "auto", cursor: "pointer" }}
            onClick={handleMenuOpen}
          >
            <PersonIcon fontSize="medium" />
            <Typography
              sx={{ fontSize: "18px" }}
              noWrap
              component="div"
              marginLeft={1}
            >
              {loggedInID}
            </Typography>
          </Box>
          <Menu
            anchorEl={anchorEl}
            keepMounted
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
          >
            <MenuItem onClick={signOut}>
              <LogoutIcon fontSize="small" />
              <Typography variant="body2" noWrap component="div" marginLeft={1}>
                サインアウト
              </Typography>
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>
      <DrawerMenu
        container={container}
        open={drawerOpen}
        onClose={handleDrawerToggle}
        width={drawerWidth}
        appVariant={appVariant}
      />
      <Box
        component="main"
        sx={{ flexGrow: 1, p: 3, width: `calc(100% - ${drawerWidth}px)` }}
      >
        <Toolbar />
      </Box>
    </Box>
  );
};

export default Header;
